// Phase 14e-a — /admin/rates/modifiers.

import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { unwrap } from '@truecare/api-client';
import { api } from '../../api';
import { SupersedableResourcePanel } from '../../components/SupersedableResourcePanel';

export const Route = createFileRoute('/rates/modifiers')({
  component: RateModifiersPage,
});

const APPLY_TO = [
  { value: 'PAY', label: 'Pay' },
  { value: 'BILL', label: 'Bill' },
  { value: 'BOTH', label: 'Both' },
] as const;

// Engine-recognised modifier codes (rate.service.ts isModifierEligible): each
// triggers a specific eligibility rule. Suggested in the code datalist; the
// operator may still type a custom code for an always-on premium/fee.
const MODIFIER_CODES = ['NIGHT', 'WEEKEND', 'STAT', 'LASTMIN'] as const;

const FORMULA = [
  { value: 'ADD', label: 'Add (flat amount)' },
  { value: 'MULTIPLY', label: 'Multiply (percentage)' },
] as const;

const CREATE_SCHEMA = z.object({
  code: z.string().min(1).max(50),
  applyTo: z.enum(['PAY', 'BILL', 'BOTH']),
  formula: z.enum(['ADD', 'MULTIPLY']),
  amount: z.string().regex(/^\d+(\.\d{1,4})?$/, 'Enter a positive decimal'),
  // Only a LASTMIN modifier uses this; blank → null in the body below. The
  // backend requires the KEY (nullable, no default), so omitting it entirely
  // 422'd every create — hence this field + the null conversion.
  lastMinuteThresholdHours: z
    .string()
    .regex(/^\d*$/, 'Whole number of hours, or blank')
    .optional(),
  effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().min(10).max(500),
  overrideLeadTime: z.boolean().optional(),
  overrideReason: z.string().optional(),
});

const SUPERSEDE_SCHEMA = z.object({
  newAmount: z.string().regex(/^\d+(\.\d{1,4})?$/),
  effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().min(10).max(500),
  overrideLeadTime: z.boolean().optional(),
  overrideReason: z.string().optional(),
});

const DEPRECATE_SCHEMA = z.object({
  effectiveTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().min(10).max(500),
  overrideLeadTime: z.boolean().optional(),
  overrideReason: z.string().optional(),
});

interface RateModifierRow {
  id: string;
  code: string;
  applyTo: string;
  formula: string;
  amount: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  isCurrent: boolean;
  inUse: boolean;
  // v1.x: modifier UI edit/delete deferred — its edit body needs the array
  // fields (appliesToRoleIds/appliesToSettings) which don't fit the generic
  // panel's field descriptors, and a partial edit would reset them. Edit/delete
  // remain available via the API (PATCH/DELETE /admin/rates/modifiers/:id); the
  // panel shows Supersede/Deprecate here (no edit/delete endpoints wired).
}

function RateModifiersPage() {
  return (
    <SupersedableResourcePanel<RateModifierRow>
      labels={{
        panelTitle: 'Rate modifiers',
        singular: 'rate modifier',
        plural: 'rate modifiers',
      }}
      columns={[
        { key: 'code', header: 'Code', width: 'w-32' },
        { key: 'applyTo', header: 'Apply to', width: 'w-28' },
        { key: 'formula', header: 'Formula', width: 'w-32' },
        { key: 'amount', header: 'Amount / ratio', width: 'w-28', render: (r) => r.amount },
        { key: 'effectiveFrom', header: 'From', width: 'w-28' },
        {
          key: 'effectiveTo',
          header: 'To',
          width: 'w-28',
          render: (r) => r.effectiveTo ?? '—',
        },
      ]}
      endpoints={{
        list: async () => {
           
          const r = (await unwrap(
            api.GET('/api/v1/admin/rates/modifiers' as never, {
              params: { query: { includeHistorical: 'true' } },
            } as never),
          )) as { items: RateModifierRow[] };
          return r;
        },
         
        create: (body) => {
          // The backend requires lastMinuteThresholdHours as a number|null (no
          // default); convert the blank/typed string. appliesToRoleIds +
          // appliesToSettings default to [] server-side, so they're fine omitted.
          const raw = (body as { lastMinuteThresholdHours?: string }).lastMinuteThresholdHours;
          return unwrap(
            api.POST('/api/v1/admin/rates/modifiers' as never, {
              body: { ...body, lastMinuteThresholdHours: raw ? Number(raw) : null },
            } as never),
          );
        },
        supersede: (id, body) =>
           
          unwrap(
            api.POST('/api/v1/admin/rates/modifiers/{id}/supersede' as never, {
              params: { path: { id } },
              body,
            } as never),
          ),
        deprecate: (id, body) =>
           
          unwrap(
            api.POST('/api/v1/admin/rates/modifiers/{id}/deprecate' as never, {
              params: { path: { id } },
              body,
            } as never),
          ),
      }}
      form={{
        createSchema: CREATE_SCHEMA,
        createFields: [
          {
            kind: 'datalist',
            name: 'code',
            label: 'Code',
            options: MODIFIER_CODES,
            help: 'The engine applies NIGHT / WEEKEND / STAT / LASTMIN only under that condition. Any other code (e.g. a general premium/fee) always applies during its effective window. Use the exact codes above — a typo like "NIGHT_PREMIUM" becomes an always-on modifier, not a night-only one.',
          },
          { kind: 'select', name: 'applyTo', label: 'Apply to', options: APPLY_TO },
          { kind: 'select', name: 'formula', label: 'Formula', options: FORMULA },
          {
            kind: 'text',
            name: 'amount',
            label: 'Amount / multiplier',
            help: 'Flat $ for ADD (e.g. 5.00); multiplier for MULTIPLY (e.g. 1.5 = +50%).',
          },
          {
            kind: 'text',
            name: 'lastMinuteThresholdHours',
            label: 'Last-minute threshold (hours)',
            help: 'ONLY for a LASTMIN modifier — applies when booked within this many hours of the shift. Leave blank for every other code.',
          },
        ],
        supersedeSchema: SUPERSEDE_SCHEMA,
        supersedeFields: [
          {
            kind: 'text',
            name: 'newAmount',
            label: 'New amount / multiplier',
            help: 'Pre-filled with the current value. Flat $ for ADD, multiplier for MULTIPLY.',
          },
        ],
        supersedeDefaultValues: (entry) => ({ newAmount: entry.amount }),
        deprecateSchema: DEPRECATE_SCHEMA,
      }}
      query={{ queryKey: ['admin', 'rates', 'modifiers'] }}
    />
  );
}
